// Typed wrappers for the go-pim /v1/companies surface.
//
// The company is go-pim's first-class tenant and carries the ISSUER identity
// (legal name, GSTIN, PAN, bank details, …) stamped onto generated documents.
//
// All calls go through the shared apiClient (bearer + 401-refresh-retry +
// json-bigint). Scope is by company id IN THE PATH — no tenant header.
import { apiClient } from "./client";

export type DocumentMode = "none" | "receipt" | "invoice" | "both";
export type Taxation = "none" | "gst" | "solar_70_30";

// ─── Human labels ─────────────────────────────────────────────────────────
//
// Operators must NEVER see the raw wire tokens. Every <select> option, pill,
// and summary renders through these maps. The keys (wire values) are the
// backend contract and are unchanged — only the presentation is humanised.

export const DOCUMENT_MODE_LABELS: Record<DocumentMode, string> = {
  none: "Don't issue documents",
  receipt: "Receipt only",
  invoice: "Invoice only",
  both: "Receipt + invoice",
};

export const TAXATION_LABELS: Record<Taxation, string> = {
  none: "No tax",
  gst: "GST",
  solar_70_30: "Solar (70/30 split)",
};

/** Ordered option lists for the <select> controls (stable, sensible order). */
export const DOCUMENT_MODES: DocumentMode[] = [
  "none",
  "receipt",
  "invoice",
  "both",
];
export const TAXATIONS: Taxation[] = ["none", "gst", "solar_70_30"];

// ─── Collection-profile (business_profile_id) link validation ──────────────
//
// The widget business_profile_id is the LOAD-BEARING link: at settle time
// widget resolves the company BY this id and asks go-pim to generate the
// document. An EMPTY value is a draft (the company issues nothing).
//
// THIS FIELD USED TO REJECT EVERY CORRECT VALUE. It required a bare 8-4-4-4-12
// UUID, but widget mints ids WITH A PREFIX —
// `id = "bp_" + uuid.NewString()` (widget/internal/handlers/panel_business_profiles.go:76)
// — and seeded ids like `bp_77623` are not UUID-shaped at all. So the only
// values the panel accepted were ones that can NEVER resolve, and the error
// copy told operators to strip the prefix that made the id correct.
//
// The failure was silent and staged. A phantom link renders the same confident
// green pill as a real one; widget's resolve returns NotFound, which it cannot
// distinguish from "go-pim is switched off", so it quietly falls back to local
// generation with the wrong issuer. Then, once the entity sync runs, the sweep
// inserts a SECOND shell company for the real profile, widget hands over to
// go-pim, and go-pim refuses it (document_mode 'none') — and that path must
// never fall back. The payer's receipt disappears entirely.
//
// So: the id is OPAQUE. go-pim stores it verbatim in a plain `text` column with
// no format check and no FK (it names a row in another service's database, so
// it cannot have one). The panel must not reconstruct a grammar it does not
// own. We assert only what is structurally true — non-empty, no whitespace, and
// a sane length — and treat the shape as a HINT, never a gate.
export const MAX_BUSINESS_PROFILE_ID_LEN = 200;

/**
 * widget's current minting convention. A hint for the operator, NOT a rule.
 *
 * Requires something after the prefix. `bp_` on its own, or `bp_x`, satisfies
 * "starts with bp_" while being obviously not an id — a truncated paste is the
 * most likely way to produce one, and it is precisely the case a prefix-only
 * check waves through in silence.
 *
 * The floor is deliberately LOW (3). widget's seeded ids are short — `bp_77623`
 * is real — and this is a warning shown next to a field an operator can still
 * submit, so a false warning on a correct id costs more trust than a missed
 * warning on a wrong one costs safety.
 */
export const BUSINESS_PROFILE_ID_HINT_RE = /^bp_\S{3,}$/i;

export function isValidBusinessProfileId(v: string): boolean {
  const t = v.trim();
  return t !== "" && t.length <= MAX_BUSINESS_PROFILE_ID_LEN && !/\s/.test(t);
}

/**
 * A soft nudge for a value that does not look like widget's convention. Returns
 * null when there is nothing to say.
 *
 * NON-BLOCKING on purpose, and this is the important part: the id names a row in
 * ANOTHER SERVICE'S DATABASE. go-pim stores it verbatim in a plain text column
 * with no FK — it cannot have one — and go-pim has no widget client to ask, by
 * design (the sync is one-way and adding a reverse call would create the loop
 * the whole ownership split exists to avoid).
 *
 * So NOTHING here, or in go-pim, can confirm that a link resolves. A stricter
 * regex does not buy correctness, it only buys a different set of false
 * rejections — which is exactly the bug this replaces, where the panel refused
 * every id widget actually mints. The real mitigations are elsewhere and are
 * already in place:
 *
 *   * a hand-linked company reads "(unconfirmed)", never a confident "Issuing"
 *   * once the entity sync runs, a real profile arrives as its OWN company with
 *     sync_state='synced', so a phantom link shows up as a duplicate row rather
 *     than passing for the real one
 */
export function businessProfileIdHint(v: string): string | null {
  const t = v.trim();
  if (t === "") return null;
  if (BUSINESS_PROFILE_ID_HINT_RE.test(t)) return null;
  return "This doesn't look like a widget business-profile id (they normally read “bp_” followed by a UUID). Check you copied the whole thing — nothing here can verify the link, and one that doesn't resolve issues nothing, silently.";
}

// ─── GST rate: percent ⇄ basis points ──────────────────────────────────────
//
// Operators think in percent (18, 2.5); the wire carries integer basis points
// (1800, 250). The conversion lives here so the components never expose "basis
// points" and the %↔bps maths is in one place.
export function bpsToPct(bps: number): string {
  // 1800 → "18", 250 → "2.5", 0 → "0". No trailing zeros.
  return String(bps / 100);
}

export function pctToBps(pct: number): number {
  return Math.round(pct * 100);
}

// ─── Issuer identity validation ────────────────────────────────────────────
//
// Mirrors go-pim's normalizeIssuer, which in turn mirrors widget's — the same
// value has to satisfy all three, and the reasoning is widget's original: a
// wrong GSTIN on a tax invoice is a compliance defect, not a cosmetic typo.
//
// WHY THIS IS WORTH DUPLICATING RATHER THAN LEAVING TO THE 400. The issuer block
// is FROZEN onto every document at generation and never re-resolved, so a
// malformed value is permanent on every document already issued — fixing the
// company row afterwards does not fix the documents. An error the operator sees
// beside the field, before they save, is worth more here than almost anywhere
// else in this panel.
//
// The server is still the authority. This is the message, not the gate.

export const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/;
export const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
export const CIN_RE = /^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/;
export const STATE_CODE_RE = /^[0-9]{2}$/;

const ISSUER_FORMATS: {
  field: keyof CompanyIssuer;
  re: RegExp;
  label: string;
  example: string;
}[] = [
  { field: "gstin", re: GSTIN_RE, label: "GSTIN", example: "29ABCDE1234F1Z5" },
  { field: "pan", re: PAN_RE, label: "PAN", example: "ABCDE1234F" },
  { field: "cin", re: CIN_RE, label: "CIN", example: "U40106KA2015PTC079146" },
  {
    field: "state_code",
    re: STATE_CODE_RE,
    label: "State code",
    example: "29",
  },
];

/**
 * Returns why this issuer field is malformed, or null when it is fine.
 *
 * EMPTY IS ALWAYS FINE. A donation trust has no GSTIN and a sole proprietor has
 * no CIN; all six production companies have an entirely empty issuer block.
 * Flagging absence would put a red field on every company that is correctly
 * configured.
 *
 * CASE IS FOLDED BEFORE CHECKING, exactly as the server folds it before storing.
 * An operator pasting a GSTIN in lower case has made no error worth flagging —
 * the value that reaches the invoice will be the canonical upper-case one.
 */
export function issuerFieldError(
  field: keyof CompanyIssuer,
  value: string | undefined,
): string | null {
  const v = (value ?? "").trim().toUpperCase();
  if (v === "") return null;
  const fmt = ISSUER_FORMATS.find((f) => f.field === field);
  if (!fmt) return null;
  if (fmt.re.test(v)) return null;
  return `Doesn't look like a ${fmt.label} (e.g. ${fmt.example}). This is printed on tax invoices and frozen onto every document issued, so it can't be corrected afterwards.`;
}

/** Every malformed issuer field on this company, for a pre-save summary. */
export function issuerErrors(issuer: CompanyIssuer): string[] {
  return ISSUER_FORMATS.map((f) =>
    issuerFieldError(f.field, issuer[f.field]),
  ).filter((e): e is string => e != null);
}

// ─── Derived "is this company actually issuing?" status ─────────────────────
//
// Pure function of a company's link + document_mode — no backend call. This is
// the single most important thing an operator needs at a glance:
//   • not linked           → warn  "Not linked"  (issues NOTHING)
//   • linked, mode = none  → ""     "Not issuing" (issues NOTHING)
//   • linked, mode ≠ none  → pos    "Issuing · <mode>"
// The tone union is identical to UI.tsx's PillTone, so the result feeds a Pill
// directly without a cast.
export type IssuingTone = "" | "pos" | "warn" | "neg" | "info";

export interface IssuingStatus {
  tone: IssuingTone;
  label: string;
}

// Everything issuingStatus reads that WIDGET owns, in one place. Callers that
// build their input from a CompanyWritePayload (the detail page derives status
// from the live form so config goes live as you type) must thread these in from
// the saved company, because a write payload deliberately carries only what an
// operator may write.
//
// Forgetting one is not hypothetical — it is how "Issuing" survived on the
// detail page in the very change that added the widget veto. So this type is
// the single list, and widgetProvenance() below is checked against it: add a
// field here and the helper stops compiling until it is threaded.
export type WidgetProvenance = {
  sync_state: SyncState;
  widget_status: string;
  widget_document_mode: string;
};

export function widgetProvenance(c: WidgetProvenance): WidgetProvenance {
  return {
    sync_state: c.sync_state,
    widget_status: c.widget_status,
    widget_document_mode: c.widget_document_mode,
  };
}

export function issuingStatus(
  c: {
    widget_business_profile_id: string | null;
    document_mode: DocumentMode;
  } & Partial<WidgetProvenance>,
): IssuingStatus {
  const linked =
    c.widget_business_profile_id != null &&
    c.widget_business_profile_id.trim() !== "";
  if (!linked) return { tone: "warn", label: "Not linked" };

  // widget has DROPPED this profile. The company and its documents are kept
  // forever — a frozen receipt must stay explicable — but it will never settle
  // another payment, and saying "Issuing" here is a lie.
  if (c.sync_state === "absent")
    return { tone: "neg", label: "Gone from widget" };

  if (c.document_mode === "none") return { tone: "", label: "Not issuing" };

  // WIDGET'S OWN MODE IS A HARD VETO, and this is the bug that made this
  // function lie. widget's pimDocumentKinds computes what IT wants first and
  // returns nothing if its mode is 'none' — before it ever reads this company.
  // So go-pim can be armed and issuing nothing.
  //
  // All six production companies read "Issuing · Receipt only" for a day while
  // widget's mode was 'none' and not one document existed. The single screen an
  // operator uses to answer "is this merchant live?" answered it wrongly, with
  // a green pill.
  //
  // Only assert this when widget has actually TOLD us. "" means unsynced or an
  // API that predates the field, and treating silence as a veto would trade one
  // confident wrong answer for another.
  if (c.widget_document_mode === "none")
    return { tone: "warn", label: "Blocked at widget" };

  // SUSPENDED OR CLOSED AT WIDGET NOW STOPS ISSUING (go-pim#36). This used to
  // read "Issuing · suspended at widget", because migration 0003 carried
  // widget_status without acting on it and the company genuinely did keep
  // issuing. go-pim refuses generation for a restricted merchant as of
  // go-pim sha-eb960b0+, so that label is now false in the most damaging
  // direction:
  // it would tell an operator documents are still being produced for a
  // merchant they deliberately cut off.
  if (c.widget_status === "suspended" || c.widget_status === "closed")
    return { tone: "neg", label: `Stopped · ${c.widget_status} at widget` };

  // A link go-pim invented rather than one widget confirmed. It may be a typo
  // that will never resolve, and nothing downstream will ever say so: widget
  // reads NotFound as "go-pim is switched off" and silently issues nothing.
  if (c.sync_state === "local")
    return {
      tone: "info",
      label: `Issuing · ${DOCUMENT_MODE_LABELS[c.document_mode]} (unconfirmed)`,
    };

  return {
    tone: "pos",
    label: `Issuing · ${DOCUMENT_MODE_LABELS[c.document_mode]}`,
  };
}

// ─── Non-blocking configuration warnings ────────────────────────────────────
//
// Soft foot-guns: the company can still be saved, but the operator should know
// a generic template will be used, or that documents will carry no issuer
// name. Pure function so it is shared by the create modal and the detail form.
export function companyWarnings(c: {
  document_mode: DocumentMode;
  invoice_template: string;
  receipt_template: string;
  issuer: CompanyIssuer;
  taxation?: Taxation;
}): string[] {
  const out: string[] = [];
  const issuesInvoice =
    c.document_mode === "invoice" || c.document_mode === "both";
  const issuesReceipt =
    c.document_mode === "receipt" || c.document_mode === "both";
  if (issuesInvoice && c.invoice_template.trim() === "")
    out.push("No invoice template selected — a generic template will be used.");
  if (issuesReceipt && c.receipt_template.trim() === "")
    out.push("No receipt template selected — a generic template will be used.");
  if (c.document_mode !== "none" && (c.issuer.legal_name ?? "").trim() === "")
    out.push("This company will issue documents without an issuer legal name.");
  // THE ONE WARNING HERE THAT IS ABOUT MONEY RATHER THAN TIDINESS.
  //
  // go-pim's Solar7030Tax carries a STAKEHOLDER-UNCONFIRMED marker: the 70/30
  // goods/services split and the 5% goods rate are the commonly cited numbers
  // and match one merchant's hand-written invoice, but the goods portion moved
  // to 12% after the 2021 rate rationalization and nobody has confirmed which
  // applies to these contracts. On the ₹85,000 worked example that is a
  // ₹3,541.67 difference in the tax PRINTED ON A TAX INVOICE.
  //
  // A warning, not a block: a company already configured this way must not
  // have its next unrelated save refused. But an operator picking it out of a
  // dropdown today gets an unverified tax computation with nothing anywhere
  // saying so, and an under-stated output tax cannot be fixed after the fact —
  // the document is frozen at issue by design. See go-pim#65.
  if (c.taxation === "solar_70_30" && c.document_mode !== "none")
    out.push(
      "The solar 70/30 tax rates are UNCONFIRMED (goods taxed at 5%; the rate moved to 12% after 2021). On a ₹85,000 invoice that is a ₹3,541.67 difference in the tax printed. Confirm with Finance before issuing — an issued document is frozen and cannot be recomputed. See go-pim#65.",
    );
  return out;
}

/**
 * CompanyIssuer — the issuer-identity block stamped onto documents. Every field
 * is optional on the wire; the form treats them all as free text (reg_80g is
 * the 80G registration identifier, bank_details a free-text block — ASSUMED
 * string shapes; adjust if the backend returns structured objects).
 */
export interface CompanyIssuer {
  legal_name?: string;
  address?: string;
  gstin?: string;
  pan?: string;
  cin?: string;
  state_code?: string;
  reg_80g?: string;
  contact?: string;
  logo_url?: string;
  bank_details?: string;
}

/**
 * Where this company came from, and whether widget still has it.
 *
 *   local  — made here, by hand. widget has never pushed it, so nothing has
 *            ever confirmed its business-profile link resolves.
 *   synced — widget pushed this profile at least once. The link is real.
 *   absent — was synced, then missing from a full snapshot. NEVER deleted:
 *            generated documents reference companies, and a frozen receipt
 *            must stay explicable forever.
 */
export type SyncState = "local" | "synced" | "absent";

export interface Company {
  id: string;
  display_name: string;
  /** Optional link to a widget business profile (nullable, UNIQUE). */
  widget_business_profile_id: string | null;

  /** ── Widget-owned mirror (migration 0003). READ-ONLY. ──
   *
   *  These four belong to widget and arrive via the entity sync. An operator
   *  edit does not stick — the next full-snapshot sweep overwrites it — so any
   *  surface that lets someone type must present them read-only for a synced
   *  company. That includes display_name above, which widget also owns:
   *  issuer.legal_name is the operator-owned string that actually PRINTS on a
   *  document.
   *
   *  Never send these on a write. The API accepts and ignores them (so a client
   *  can round-trip a response without a 400), but they are widget's to move. */
  /** ISO 18245 merchant category code, four digits. TEXT, not a number:
   *  leading zeros are significant — 0742 is veterinary services, and treating
   *  it as a number would store 742 and mislabel the merchant forever. */
  mcc: string;

  legal_entity_id: string;
  widget_status: string;
  /** widget's OWN document_mode — a HARD VETO applied before widget ever reads
   *  this company. "" means widget has not said, which is DISTINCT from an
   *  explicit "none": unknown and explicitly-nothing must not render alike. */
  widget_document_mode: string;
  sync_state: SyncState;
  /** RFC3339, or "" when widget has never pushed this company. */
  widget_synced_at: string;
  issuer: CompanyIssuer;
  document_mode: DocumentMode;
  taxation: Taxation;
  /** Default GST rate in basis points (integer; 1800 = 18%). */
  default_gst_rate_bps: number;
  /** Template keys from GET /v1/templates. */
  invoice_template: string;
  receipt_template: string;

  /** Delivery identity — where this merchant's receipts live and how its mail
   *  leaves. Empty means "fall back to the platform default". */
  public_base_url: string;
  email_domain: string;
  notification_brand: string;

  /** Sender identity. from_address must sit on email_domain above. */
  from_name: string;
  from_address: string;
  reply_to: string;

  created_at: string;
  updated_at: string;
}

/** Fields accepted by POST /v1/companies and PUT /v1/companies/:id. */
export interface CompanyWritePayload {
  display_name: string;
  widget_business_profile_id: string | null;
  /** Four digits, or "" for uncategorised. See Company.mcc. */
  mcc: string;
  issuer: CompanyIssuer;
  document_mode: DocumentMode;
  taxation: Taxation;
  default_gst_rate_bps: number;
  invoice_template: string;
  receipt_template: string;

  /** Delivery + sender.
   *
   *  ALWAYS SEND THESE. The API treats an ABSENT key as "preserve" and an empty
   *  string as "clear" — a distinction that exists because this payload is a
   *  full-row replace and an earlier build of this panel, which did not know
   *  about these fields, was blanking them on every unrelated company save.
   *  Sending them explicitly keeps the round-trip honest: what the form shows is
   *  what gets stored. */
  public_base_url: string;
  email_domain: string;
  notification_brand: string;
  from_name: string;
  from_address: string;
  reply_to: string;
}

export interface ListCompaniesParams {
  limit?: number;
  offset?: number;
}

/**
 * listCompanies — GET /v1/companies?limit=&offset=
 *
 * ASSUMED SHAPE: `{ companies: Company[] }`. Adjust the unwrap if go-pim
 * returns a bare array.
 */
export async function listCompanies(
  params: ListCompaniesParams = {},
): Promise<Company[]> {
  const resp = await apiClient.get<{ companies: Company[] }>("/v1/companies", {
    params,
  });
  return resp.data.companies ?? [];
}

/** getCompany — GET /v1/companies/:id */
export async function getCompany(id: string): Promise<Company> {
  const resp = await apiClient.get<Company>(`/v1/companies/${id}`);
  return resp.data;
}

/** getCompanyByBusinessProfile — GET /v1/companies/by-business-profile/:bpid */
export async function getCompanyByBusinessProfile(
  bpid: string,
): Promise<Company> {
  const resp = await apiClient.get<Company>(
    `/v1/companies/by-business-profile/${bpid}`,
  );
  return resp.data;
}

/** createCompany — POST /v1/companies */
export async function createCompany(
  payload: CompanyWritePayload,
): Promise<Company> {
  const resp = await apiClient.post<Company>("/v1/companies", payload);
  return resp.data;
}

/** updateCompany — PUT /v1/companies/:id (PUT, not PATCH). */
export async function updateCompany(
  id: string,
  payload: CompanyWritePayload,
): Promise<Company> {
  const resp = await apiClient.put<Company>(`/v1/companies/${id}`, payload);
  return resp.data;
}
